import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ConnectedOverlayPositionChange,
  ConnectionPositionPair,
  OverlayModule,
  VerticalConnectionPos,
} from '@angular/cdk/overlay';
import { BoschUiDropdownWidthDirective } from '../directives/bosch-ui-dropdown-width.directive';
import { BehaviorSubject, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'bosch-ui-dropdown-desktop',
  standalone: true,
  imports: [CommonModule, OverlayModule, BoschUiDropdownWidthDirective],
  templateUrl: './bosch-ui-dropdown-desktop.component.html',
  styleUrls: ['./bosch-ui-dropdown-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoschUiDropdownDesktopComponent implements OnInit, OnDestroy {
  @Input() set open(isOpen: boolean) {
    this._isOpen$.next(isOpen);
  }
  @Input() customDropdownListWidth = 0;
  @Input() set dropdownOffsetY(dropdownOffsetY: number) {
    this.dropdownOffsetY$.next(dropdownOffsetY);
  }
  @Input() dropdownPositions: ConnectionPositionPair[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];
  @Output() dropdownChange: EventEmitter<boolean> = new EventEmitter();
  @Output() dropdownPositionChange: EventEmitter<VerticalConnectionPos> = new EventEmitter();

  private _isOpen$ = new BehaviorSubject(false);
  private _contentWidth$ = new BehaviorSubject(0);
  private _contentPosition$ = new BehaviorSubject<VerticalConnectionPos>('bottom');
  private _unsubscribe$: Subject<void> = new Subject<void>();

  isOpen$ = this._isOpen$.asObservable();
  contentWidth$ = this._contentWidth$.asObservable();
  dropdownOffsetY$ = new BehaviorSubject<number>(0);

  constructor(private cdRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.dropdownPositionChange.next(this.dropdownPositions[0].originY);

    this._isOpen$.pipe(takeUntil(this._unsubscribe$)).subscribe((isOpen: boolean) => this.dropdownChange.next(isOpen));
    this._contentPosition$
      .pipe(takeUntil(this._unsubscribe$), distinctUntilChanged())
      .subscribe((position: VerticalConnectionPos) => {
        const offsetY = Math.abs(this.dropdownOffsetY$.getValue());

        this.dropdownPositionChange.next(position);
        this.dropdownOffsetY$.next(position === 'top' ? -offsetY : offsetY);
        this.cdRef.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
  }

  onOpen(): void {
    this._isOpen$.next(!this._isOpen$.getValue());
  }

  onOutsideClick(): void {
    this._isOpen$.next(false);
  }

  onWidthChange(contentWidth: number): void {
    this._contentWidth$.next(contentWidth);
  }

  onPositionChange(change: ConnectedOverlayPositionChange): void {
    const position = change.connectionPair.originY;

    this._contentPosition$.next(position);
  }
}
